import React from "react";
import { useInfiniteContacts } from "@/hooks/useInfiniteContacts";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { getDisplayName, getContactPhone, formatWhatsAppJid, formatCreatedDate, generateContactAvatar } from "@/utils/contactUtils";
import Table from "@/components/ui/shared/table/Table";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableCell from "@/components/ui/shared/table/TableCell";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import TableRow from "@/components/ui/shared/table/TableRow";
import { Switch } from "@/components/ui/switch";
import ContactEdit from "./ContactEdit";
import { Badge } from "@/components/ui/badge";
import { useAssistants } from "@/hooks/useAssistants";
import { useTeam } from "@/hooks/useTeam";
import { useState, useEffect, useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import type { ContactFilters } from "@/components/ui/shared/MoreFilter";
import type { SortOption } from "@/components/contatos/FilterHeader";

interface ContactsTableProps {
  searchTerm?: string;
  filters?: ContactFilters;
  sortBy?: SortOption;
}

const ContactsTable: React.FC<ContactsTableProps> = ({ searchTerm = "", filters, sortBy = 'created_desc' }) => {
  const { contacts, initialLoading, loadingMore, error, refetch, updateContact, hasMore, loadMore, totalCount } = useInfiniteContacts({ searchTerm });
  const { user } = useAuth();
  const { assistants } = useAssistants();
  const { members } = useTeam();
  const [localError, setLocalError] = useState<string | null>(null);

  const filteredContacts = useMemo(() => {
    let filtered = [...contacts];

    // Busca por searchTerm agora é feita no banco de dados via useInfiniteContacts
    // Mantenha apenas os filtros locais abaixo

    if (filters) {
      if (filters.assistantId && filters.assistantId !== '__clear__') {
        filtered = filtered.filter(contact =>
          contact.account_assistant_id === filters.assistantId
        );
      }

      if (filters.attributionId && filters.attributionId !== '__clear__') {
        filtered = filtered.filter(contact =>
          contact.attribution_id === filters.attributionId
        );
      }

      if (filters.tags.length > 0 && filters.tags[0] !== '__clear__') {
        filtered = filtered.filter(contact => {
          const contactTags = (contact as any).tags;
          if (!Array.isArray(contactTags) || contactTags.length === 0) return false;
          return filters.tags.some(filterTag => contactTags.includes(filterTag));
        });
      }

      if (filters.campaigns.length > 0 && filters.campaigns[0] !== '__clear__') {
        filtered = filtered.filter(contact => {
          const contactCampaigns = (contact as any).campaigns;
          if (!Array.isArray(contactCampaigns) || contactCampaigns.length === 0) return false;
          return filters.campaigns.some(filterCampaign => contactCampaigns.includes(filterCampaign));
        });
      }

      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        filtered = filtered.filter(contact => {
          if (!contact.created_at) return false;
          const contactDate = new Date(contact.created_at);
          contactDate.setHours(0, 0, 0, 0);
          return contactDate >= fromDate;
        });
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        filtered = filtered.filter(contact => {
          if (!contact.created_at) return false;
          const contactDate = new Date(contact.created_at);
          return contactDate <= toDate;
        });
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'created_desc':
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        case 'created_asc':
          return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
        case 'name_asc':
          return getDisplayName(a).localeCompare(getDisplayName(b));
        case 'name_desc':
          return getDisplayName(b).localeCompare(getDisplayName(a));
        default:
          return 0;
      }
    });

    console.log('🔍 [ContactsTable] Filtering and sorting contacts:', {
      filters,
      sortBy,
      total_contacts: contacts.length,
      filtered_count: filtered.length,
      has_active_filters: (filters && (
        filters.assistantId ||
        filters.attributionId ||
        filters.tags.length > 0 ||
        filters.campaigns.length > 0 ||
        filters.dateFrom ||
        filters.dateTo
      ))
    });

    return filtered;
  }, [contacts, filters, sortBy]);

  const handleAssistantToggle = async (contactId: string, currentAssistantId: string | null) => {
    const contact = contacts.find(c => c.id === contactId);
    if (!contact || !user) return;

    const newStatus = !contact.assistant_is_active;

    console.log('🔄 [ContactsTable] Toggling assistant status:', {
      contact_id: contactId,
      current_status: contact.assistant_is_active,
      new_status: newStatus
    });

    try {
      const success = await updateContact(contactId, { assistant_is_active: newStatus });

      if (!success) {
        console.error('❌ [ContactsTable] Erro ao atualizar status do assistente');
        setLocalError('Erro ao atualizar status do assistente');
        return;
      }

      console.log('✅ [ContactsTable] Status do assistente atualizado com sucesso');
      setLocalError(null);

    } catch (err) {
      console.error('💥 [ContactsTable] Erro inesperado:', err);
      setLocalError(`Erro inesperado: ${err instanceof Error ? err.message : 'Erro desconhecido'}`);
    }
  };

  console.log('🎯 [ContactsTable] Renderizando tabela de contatos:', {
    contacts_count: contacts.length,
    filtered_count: filteredContacts.length,
    total_count: totalCount,
    has_more: hasMore,
    initialLoading,
    loadingMore,
    error: error || localError
  });

  if (initialLoading) {
    return (
      <div className="flex-1 min-h-0 mt-3">
        <div className="w-full h-full flex items-center justify-center border-[1px] border-[#3F3F464D] rounded-[12px] bg-gray-500">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
            <p className="text-neutral-50">Carregando contatos...</p>
          </div>
        </div>
      </div>
    );
  }

  const displayError = error || localError;

  if (displayError) {
    return (
      <div className="flex-1 min-h-0 mt-3">
        <div className="w-full h-full flex items-center justify-center border-[1px] border-[#3F3F464D] rounded-[12px] bg-gray-500">
          <div className="flex flex-col items-center gap-4 text-center p-6">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-50 mb-2">Erro ao carregar contatos</h3>
              <p className="text-neutral-300 text-sm mb-4">{displayError}</p>
              <button
                onClick={refetch}
                className="px-4 py-2 bg-neutral-600 hover:bg-neutral-700 text-neutral-50 rounded-md transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="flex-1 min-h-0 mt-3">
        <div className="w-full h-full flex items-center justify-center border-[1px] border-[#3F3F464D] rounded-[12px] bg-gray-500">
          <div className="flex flex-col items-center gap-4 text-center p-6">
            <div className="w-16 h-16 bg-neutral-600 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-50 mb-2">Nenhum contato encontrado</h3>
              <p className="text-neutral-300 text-sm">
                Quando você receber mensagens no WhatsApp, os contatos aparecerão aqui.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Detectar se há filtros ativos (searchTerm é tratado no banco de dados)
  const hasActiveFilters = (filters && (
    filters.assistantId ||
    filters.attributionId ||
    filters.tags.length > 0 ||
    filters.campaigns.length > 0 ||
    filters.dateFrom ||
    filters.dateTo
  ));

  if (filteredContacts.length === 0) {
    return (
      <div className="flex-1 min-h-0 mt-3">
        <div className="w-full h-full flex items-center justify-center border-[1px] border-[#3F3F464D] rounded-[12px] bg-gray-500">
          <div className="flex flex-col items-center gap-4 text-center p-6">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-50 mb-2">
                {searchTerm.trim() || hasActiveFilters ? 'Nenhum contato encontrado' : 'Nenhum contato encontrado'}
              </h3>
              <p className="text-neutral-300 text-sm mb-4">
                {searchTerm.trim()
                  ? `Nenhum contato corresponde à busca "${searchTerm}".`
                  : hasActiveFilters
                    ? 'Tente ajustar ou limpar os filtros para ver mais resultados.'
                    : 'Aguardando contatos...'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quando há filtros locais ativos, desabilitar carregamento infinito
  // porque os filtros são aplicados no cliente após carregar os dados
  // SearchTerm NÃO desabilita pois é filtrado no banco de dados
  const shouldLoadMore = !hasActiveFilters && hasMore;

  return (
    <div className="flex-1 min-h-0 mt-3">
      <div
        id="contacts-scroll-container"
        className="w-full max-h-full overflow-auto border-[1px] border-[#3F3F464D] rounded-[12px] bg-gray-500 table-scrollbar"
        style={{ height: 'calc(100vh - 280px)' }}
      >
        <InfiniteScroll
          dataLength={filteredContacts.length}
          next={loadMore}
          hasMore={shouldLoadMore}
          loader={
            <div className="flex justify-center items-center py-8">
              <div className="flex flex-col items-center gap-3">
                <div className="w-6 h-6 border-3 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
                <p className="text-neutral-300 text-sm">Carregando mais contatos...</p>
              </div>
            </div>
          }
          endMessage={
            <div className="flex justify-center items-center py-6">
              <div className="flex flex-col items-center gap-2">
                <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-neutral-300 text-sm font-medium">
                  {hasActiveFilters ? 'Exibindo resultados filtrados' : 'Todos os contatos foram carregados'}
                </p>
                <p className="text-neutral-400 text-xs">
                  {hasActiveFilters
                    ? `${filteredContacts.length} de ${totalCount} contatos`
                    : `Total: ${totalCount} contatos`
                  }
                </p>
              </div>
            </div>
          }
          scrollableTarget="contacts-scroll-container"
        >
          <Table className="min-w-[1200px]">
            <TableHeader className="border-b-[1px] border-[#3F3F464D] sticky top-0 bg-gray-500 z-10">
              <TableRow>
                {["Contato", "Telefone", "Tags", "Data de Cadastro", "Atribuído", "Assistente", "Assistente Ativo", "Ações"].map((item, index) => {
                  return (
                    <TableCell key={index} as="th">
                      <div className="flex justify-start items-center gap-1">
                        <p>{item}</p>
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
            {filteredContacts.map((contact) => {
              console.log('🎯 [ContactsTable] Renderizando contato:', {
                id: contact.id,
                jid: contact.jid,
                display_name: contact.display_name,
                push_name_last: contact.push_name_last,
                is_group: contact.is_group,
                last_message_at: contact.last_message_at,
                account_assistant_id: contact.account_assistant_id,
                assistant_name: (contact as any).assistant?.name
              });

              const displayName = getDisplayName(contact);
              const contactPhone = getContactPhone(contact);
              const createdDate = formatCreatedDate(contact.created_at);
              const avatarUrl = generateContactAvatar(contact);
              const assignedAssistant = assistants.find(a => a.id === contact.account_assistant_id);
              const attributedMember = members.find(m => m.auth_user_id === contact.attribution_id);

              const contactTags = (contact as any).tags || [];
              const visibleTags = contactTags.slice(0, 2);
              const remainingCount = contactTags.length - 2;

              return (
                <TableRow key={contact.id}>
                  <TableCell as="td">
                    <div className="flex flex-col">
                      <p className="font-medium">{displayName}</p>
                      {contact.is_group && (
                        <Badge variant="default2" className="text-xs w-fit mt-1">
                          Grupo
                        </Badge>
                      )}
                    </div>
                  </TableCell>

                  <TableCell as="td">
                    {contactPhone}
                  </TableCell>

                  <TableCell as="td">
                    {contactTags.length === 0 ? (
                      <span className="text-neutral-300 text-sm">-</span>
                    ) : (
                      <div className="flex items-center gap-1 flex-wrap max-w-[200px]">
                        {visibleTags.map((tag: string, idx: number) => (
                          <Badge key={idx} variant="default" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {remainingCount > 0 && (
                          <Badge variant="default" className="text-xs">
                            +{remainingCount}
                          </Badge>
                        )}
                      </div>
                    )}
                  </TableCell>

                  <TableCell as="td">
                    {createdDate}
                  </TableCell>

                  <TableCell as="td">
                    {attributedMember ? (
                      <div className="flex flex-col">
                        <span className="text-neutral-50 text-sm font-medium">{attributedMember.name}</span>
                        <span className="text-neutral-400 text-xs">{attributedMember.email}</span>
                      </div>
                    ) : (
                      <span className="text-neutral-300 text-sm">-</span>
                    )}
                  </TableCell>

                  <TableCell as="td">
                    {assignedAssistant ? (
                      <div className="flex flex-col">
                        <span className="text-neutral-50 text-sm font-medium">{assignedAssistant.name}</span>
                        <span className="text-neutral-400 text-xs">
                          {assignedAssistant.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    ) : (
                      <span className="text-neutral-300 text-sm">Nenhum assistente</span>
                    )}
                  </TableCell>

                  <TableCell as="td">
                    <Switch 
                      checked={contact.assistant_is_active || false}
                      onCheckedChange={() => handleAssistantToggle(contact.id, contact.account_assistant_id)}
                      activeColor="data-[state=checked]:bg-[#22c55e]"
                      inactiveColor="data-[state=unchecked]:bg-[#3F3F46]"
                      thumbColor="bg-white"
                    />
                  </TableCell>

                  <TableCell as="td">
                    <ContactEdit contact={contact} />
                  </TableCell>
                </TableRow>
              );
            })}
            </TableBody>
          </Table>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default ContactsTable;